<script lang="ts">
    import {browser} from '$app/environment';
    import Breadcrumbs from '$lib/components/Breadcrumbs.svelte';
    import {now} from '$lib/stores/now';
    import {tError} from '$lib/toast-utils';
    import {date_pretty, getImageLink} from '$lib/utils';
    import {asLink} from '$lib/utils-svelte';
    import {onMount} from 'svelte';
    import GoodRow from '../../../../my/parser/result/[queueId]/GoodRow.svelte';

    export let supplier: SupplierDetailItem;

    $: is_free = supplier.free_until > $now.ts;

    let goods_loading = true;
    let goods: GoodItem[] = [];

    onMount(() => {
        loadGoods();
    });

    async function loadGoods() {
        try {
            goods_loading = true;
            const res = await fetch(`${import.meta.env.VITE_RAPI_URL}/website/${supplier.website.id}/random-goods?count=10`, {
                credentials: 'include',
            });

            if (!res.ok) {
                throw new Error('Не удается отправить запрос');
            }

            const json: {error?: string; success?: boolean; result?: GoodItem[]} = await res.json();

            if (json.error) {
                return tError(json.error || 'Неизвестная ошибка');
            }

            if (json.success && json.result) {
                goods = json.result;
                return;
            }

            console.error(res);
            tError('Неизвестная ошибка');
        } catch (e: any) {
            console.error(e);
            tError(e.message);
        } finally {
            goods_loading = false;
        }
    }
</script>

<div class="container max-w-3xl p-4">
    <div class="mb-4">
        <Breadcrumbs
            list={[
                {title: 'Каталог сайтов', url: '/catalog'},
                {title: supplier.city_list[0]?.title || '', url: `/catalog/${supplier.city_list[0]?.slug}`},
                {
                    title: supplier.category_list[0]?.title || '',
                    url: `/catalog/${supplier.city_list[0]?.slug}/${supplier.category_list[0]?.slug}`,
                },
                {title: supplier.title},
            ]}
        />
    </div>

    <div class="space-y-8" data-id={supplier.id}>
        <div class="flex flex-col gap-3 sm:flex-row">
            <div class="w-full h-auto sm:w-24 sm:h-24 grow-0 shrink-0">
                <img class="mx-auto w-36 sm:w-full" alt={supplier.title} src={getImageLink(supplier.logo, '144x144rtr')} />
            </div>

            <div class="flex flex-col w-full gap-3 grow-1">
                <h1 class="mb-0 page-title">
                    {supplier.title}
                </h1>

                <div class="flex flex-row flex-wrap items-center gap-4 max-sm:justify-between">
                    <button class="link" use:asLink={`http://${supplier.website.domain}`}>{supplier.website.domain}</button>

                    <div class="flex gap-2">
                        {#if supplier?.social?.vkcom}
                            <a
                                class="fill-black-500 stroke-black-500 hover:fill-green hover:stroke-green"
                                href={supplier.social.vkcom}
                                rel="nofollow"
                                target="_blank"
                            >
                                <svg height="25" viewBox="0 0 24 24" width="25" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        class="svg-icon"
                                        d="M17.9 16h-1.7s-.4 0-.8-.2c-.6-.4-1.2-1.5-1.7-1.4-.5.1-.4 1.1-.4 1.1s0 .2-.1.3c-.2.2-.5.2-.5.2h-.8s-1.7.1-3.2-1.4C7 13 5.5 9.8 5.5 9.8s-.1-.2 0-.3c.1-.1.4-.1.4-.1h1.9s.2 0 .3.1c.1.1.1.2.1.2s.4.6.8 1.3c.8 1.3 1.1 1.5 1.4 1.4.4-.2.2-1.8.2-1.8s0-.6-.2-.8c-.1-.2-.4-.3-.6-.3-.1 0 0-.2.3-.3.4-.2 1-.2 1.7-.2.5 0 .7 0 .9.1.6.1.4.7.4 2.1 0 .4-.1 1 .2 1.3.1.1.5 0 1.3-1.4.4-.7.7-1.4.7-1.4s0-.1.1-.2h2.1s.6 0 .7.2c.1.3-.2.9-1 1.9-1.3 1.6-1.4 1.5-.3 2.4 1 .9 1.2 1.4 1.3 1.4.6.6-.3.6-.3.6z"
                                        stroke="none"
                                    />
                                    <path
                                        class="svg-icon"
                                        d="M20 2c1.1 0 2 .9 2 2v16c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h16m0-2H4C1.8 0 0 1.8 0 4v16c0 2.2 1.8 4 4 4h16c2.2 0 4-1.8 4-4V4c0-2.2-1.8-4-4-4z"
                                        fill-rule="evenodd"
                                        stroke="none"
                                    />
                                </svg>
                            </a>
                        {/if}
                        {#if supplier?.social?.okru}
                            <a
                                class="fill-black-500 stroke-black-500 hover:fill-green hover:stroke-green"
                                href={supplier.social.okru}
                                rel="nofollow"
                                target="_blank"
                            >
                                <svg height="25" viewBox="0 0 27 27" width="25" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        class="svg-border"
                                        d="M5 1h16c2.209 0 4 1.79 4 4v16c0 2.209-1.791 3.999-4 3.999H5c-2.209 0-4-1.79-4-3.999V5c0-2.21 1.791-4 4-4z"
                                        fill="none"
                                        stroke-width="2"
                                    />
                                    <path
                                        class="svg-icon"
                                        d="M16.809 13.55c-.89 1.009-2.009 1.562-3.186 1.725l2.038 2.311c.287.293.287.813 0 1.139-.144.163-.345.228-.517.228-.201 0-.373-.065-.516-.228l-1.637-1.855-1.636 1.855c-.259.326-.718.326-1.005 0-.287-.293-.287-.814 0-1.139l2.038-2.311c-1.148-.13-2.296-.716-3.186-1.725-.287-.293-.259-.814 0-1.139.258-.325.717-.325 1.004 0 1.551 1.757 4.048 1.757 5.599 0 .258-.325.717-.325 1.004 0 .259.325.259.846 0 1.139zm-3.818-1.543c-1.648 0-2.996-1.351-2.996-3.004s1.348-3.005 2.996-3.005c1.647 0 2.995 1.352 2.995 3.005 0 1.653-1.348 3.004-2.995 3.004zm0-4.506c-.839 0-1.498.661-1.498 1.502s.659 1.503 1.498 1.503 1.498-.662 1.498-1.503c0-.841-.659-1.502-1.498-1.502z"
                                        fill-rule="evenodd"
                                        stroke="none"
                                    />
                                </svg>
                            </a>
                        {/if}
                        {#if false && supplier?.social?.fbcom}
                            <a
                                class="fill-black-500 stroke-black-500 hover:fill-green hover:stroke-green"
                                href={supplier.social.fbcom}
                                rel="nofollow"
                                target="_blank"
                            >
                                <svg height="25" viewBox="0 0 27 27" width="25" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        class="svg-border"
                                        d="M5 1h16c2.209 0 4 1.79 4 4v16c0 2.209-1.791 3.999-4 3.999H5c-2.209 0-4-1.79-4-3.999V5c0-2.21 1.791-4 4-4z"
                                        fill="none"
                                        stroke-width="2"
                                    />
                                    <path
                                        class="svg-icon"
                                        d="M14.458 8.475H16V6h-1.813v.009c-2.197.084-2.647 1.425-2.687 2.835h-.004v1.235H10v2.424h1.496v7.496h2.254v-7.496h1.847l.357-2.424h-2.203v-.747c0-.476.292-.857.707-.857z"
                                        fill-rule="evenodd"
                                        stroke="none"
                                    />
                                </svg>
                            </a>
                        {/if}
                        {#if false && supplier?.social?.instagram}
                            <a
                                class="fill-black-500 stroke-black-500 hover:fill-green hover:stroke-green"
                                href={supplier.social.instagram}
                                rel="nofollow"
                                target="_blank"
                            >
                                <svg height="25" viewBox="0 0 27 27" width="25" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        class="svg-border"
                                        d="M5 1h16c2.209 0 4 1.79 4 4v16c0 2.209-1.791 3.999-4 3.999H5c-2.209 0-4-1.79-4-3.999V5c0-2.21 1.791-4 4-4z"
                                        fill="none"
                                        stroke-width="2"
                                    />
                                    <path
                                        class="svg-icon"
                                        d="M19.503 8.005c-.826 0-1.496-.672-1.496-1.503 0-.83.67-1.502 1.496-1.502C20.33 5 21 5.672 21 6.502c0 .831-.67 1.503-1.497 1.503zM13.245 19C9.802 19 7 16.186 7 12.727c0-3.458 2.802-6.272 6.245-6.272 3.444 0 6.246 2.814 6.246 6.272 0 3.459-2.802 6.273-6.246 6.273zm0-10.105c-2.107 0-3.816 1.716-3.816 3.832 0 2.117 1.709 3.833 3.816 3.833 2.108 0 3.817-1.716 3.817-3.833 0-2.116-1.709-3.832-3.817-3.832z"
                                        fill-rule="evenodd"
                                        stroke="none"
                                    />
                                </svg>
                            </a>
                        {/if}
                    </div>
                </div>

                <div class="flex justify-center sm:justify-start">
                    <a data-tour="catalog-parser-start" class="btn btn-green h-14 !flex-col" href="/my/parser/{supplier.website.domain}"
                        >Начать загрузку
                        {#if is_free}
                            <br />
                            <span class="text-sm">
                                <b>Бесплатно</b> до {date_pretty(supplier.free_until)}
                            </span>
                        {/if}</a
                    >
                </div>
            </div>
        </div>

        <div class="prose max-w-none">
            <ul>
                <li>
                    <b>Категории:</b>
                    {supplier.category_list.map(v => v.title).join(', ')}
                </li>
                <li>
                    <b>Город:</b>
                    {supplier.city_list.map(v => v.title).join(', ')}
                </li>
                {#if supplier.email}
                    <li>
                        <b>E-mail:</b>
                        {#if browser}<a href="mailto:{supplier.email}">{supplier.email}</a>{/if}
                    </li>
                {/if}
                {#if supplier.phone}
                    <li>
                        <b>Телефон:</b>
                        {#if browser}{supplier.phone}{/if}
                    </li>
                {/if}
                {#if supplier.address}
                    <li>
                        <b>Адрес:</b>
                        {#if browser}{supplier.address}{/if}
                    </li>
                {/if}
            </ul>

            {@html supplier.description || ''}

            {#if supplier?.stats?.goods_count || supplier?.stats?.goods_pph}
                <h3>Информация о парсинге сайта</h3>

                <ul>
                    <li>
                        <b>Среднее число товаров:</b>
                        {supplier.stats.goods_count}<br />
                        <span class="text-sm italic text-black-500">Число товаров, которое в среднем парсят пользователи</span>
                    </li>
                    <li>
                        <b>Скорость парсинга:</b>
                        {supplier.stats.goods_pph} в час<br />
                        <span class="text-sm italic text-black-500">Число товаров, которое в среднем парсится за час реального времени</span
                        >
                    </li>
                </ul>
                <p class="italic">
                    Ознакомительная информация на основе данных последних парсингов сайта. Может отличаться от фактических значений
                </p>
            {/if}
        </div>
    </div>
</div>

{#if goods.length > 0}
    <div class="container p-4">
        <div class="page-title">Пример парсинга товаров</div>
        <div>
            {#each goods as good}
                <GoodRow
                    {good}
                    removable={false}
                    currency={{id: '', updated: 0, title: good.currency ?? '', symbol: good.currency ?? '', sort: 0, rate: 1}}
                />
            {/each}
        </div>
    </div>
{/if}
